import React, { useState } from 'react'
import { CheckIcon, CopyIcon, ExternalLinkIcon, InfoIcon } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'

import { alpha, Box, IconButton, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { Card } from '@repo/ui'
import { InfoTooltip, StatusChip, SystemGeneratedBadge } from 'components'
import { useAuth } from 'hooks'
import { SUPPORT_REQUEST_STATUSES, SUPPORT_REQUEST_TYPES, SUPPORT_SOURCE_LABELS } from 'constants/statuses'
import { relativeOrDateTime } from 'utils'
import { cloudLoggingUrl, CLOUD_LOGGING_PROJECT, errorRefsOf } from 'utils/supportReportSource'
import type { SupportRequestDetail, SupportMessage } from 'services/supportApi'
import { ROUTES } from 'routes'

import { displayStatusKey, SUPPORT_DISPLAY_STATUSES } from '../displayStatus'

import { AttachmentList } from './AttachmentList'
import { ReplyComposer, type ReplyPayload } from './ReplyComposer'

/**
 * The conversation on a request. Used by BOTH `/support/:id` and
 * `/internal/support/:id` (VRG-226 — C2 + C4).
 *
 * This component is the "reuse the same components with scoping removed" rule
 * made concrete on the UI side: the internal page renders exactly this, with
 * `showCompany` on and its own action bar above it. If the two views had been
 * built separately they would already be drifting — one would grow a field the
 * other lacks, and a staffer would be answering a request they cannot see the
 * same way the customer does.
 *
 * `statuses` is a parameter because the same status reads differently from each
 * side of the desk: `waiting_on_customer` is "Waiting on you" to a brand and
 * "Waiting on customer" to Vera.
 */
export const RequestThread = ({
  request,
  showCompany = false,
  statuses = SUPPORT_REQUEST_STATUSES,
  preferBoardStatus = false,
  reply,
}: {
  request: SupportRequestDetail
  showCompany?: boolean
  statuses?: Record<string, { label: string; color: string }>
  /**
   * The reply composer, when this caller wants the thread to own it.
   *
   * OPT-IN rather than automatic, and the reason is ownership rather than taste. The
   * internal queue renders its own composer today, so a composer that appeared here
   * unconditionally would give that page two — one from the thread and one below it.
   * Passing the mutation in also keeps this component free of a react-query hook it
   * would have to choose between two of: the customer reply and the staff reply are
   * different endpoints with different cache invalidation.
   *
   * `ReplyComposer` is exported, so adopting it from the internal page is an import
   * and a prop — not a second implementation. Do that rather than growing a copy;
   * the upload rules it encodes are what drift (see its docblock).
   */
  reply?: {
    label: string
    placeholder: string
    onSend: (payload: ReplyPayload) => Promise<unknown>
    isPending?: boolean
  }
  /**
   * Show the BOARD status on a promoted feature, as the list does (VRG-324).
   *
   * Opt-in rather than automatic, and the asymmetry is the point. On the CUSTOMER
   * side the list and the detail page must agree — a row that says "Planned" in
   * the list and "Open" when opened is the two-vocabularies confusion showing up
   * inside one session. On the INTERNAL side a staffer is looking at the request
   * status precisely because they act on it (mark resolved, waiting on customer),
   * so replacing it there would hide the field they came to change.
   */
  preferBoardStatus?: boolean
}) => {
  const theme = useTheme()
  // Whose request this is, from the authenticated profile rather than from the route — see the
  // note at the "From:" line.
  const { profile } = useAuth()
  const isOwnRequest = Boolean(profile?.id && request.userId && profile.id === request.userId)

  /*
    VRG-378 build context, flattened to the lines worth showing.

    Built here rather than inline so the JSX stays a list render instead of five
    nested conditionals, and so the ORDER is stated once: surface, then build, then
    the surface-specific detail — widest fact first, which is the order someone
    diagnosing reads them in.

    Every line is omitted when its value is missing, because "Version: unknown" is
    a row that costs attention and carries nothing. That is also why `Reported from`
    is skipped for `platform`: on a page inside the platform, the surface is the one
    thing that could never be in doubt.

    `clientContext` is deliberately not dumped wholesale. It is an open map by
    design, so rendering every key would put whatever a future client happens to
    send in front of a customer. The keys named below are the ones that answer a
    question a human is actually asking.
  */
  const buildLines: string[] = []
  {
    const source = request.source ?? 'platform'
    if (source !== 'platform') {
      buildLines.push(`Reported from: ${SUPPORT_SOURCE_LABELS[source] ?? source}`)
    }
    if (request.environment) buildLines.push(`Environment: ${request.environment}`)
    if (request.appVersion) buildLines.push(`Version: ${request.appVersion}`)

    const ctx = (request.clientContext ?? {}) as Record<string, unknown>
    const str = (key: string): string | null => {
      const value = ctx[key]
      return typeof value === 'string' && value.trim().length ? value : null
    }

    const runtime = str('runtimeVersion')
    // Named "Runtime" rather than "Runtime version" because the distinction being
    // drawn is against the app version directly above it, and the shorter label
    // makes the pair scan as a pair.
    if (runtime) buildLines.push(`Runtime: ${runtime}`)

    const update = str('updateId')
    if (update) {
      // The embedded case is worth saying out loud: it means no OTA update has
      // landed on that device, which is the first thing you would otherwise go and
      // check by hand after seeing a bug you thought was already fixed.
      const embedded = ctx.isEmbeddedLaunch === true ? ' (embedded)' : ''
      buildLines.push(`Update: ${update}${embedded}`)
    }

    const device = [str('platform'), str('osVersion')].filter(Boolean).join(' ')
    if (device) buildLines.push(`Device: ${device}`)

    const session = str('scanSessionId')
    if (session) buildLines.push(`Scan session: ${session}`)

    const viewport = str('viewport')
    if (viewport) buildLines.push(`Viewport: ${viewport}`)

    // VRG-721.
    const language = str('language')
    if (language) buildLines.push(`Language: ${language}`)
    const previousRoute = str('previousRoute')
    if (previousRoute) buildLines.push(`Previous page: ${previousRoute}`)
  }

  const renderMessage = (message: SupportMessage) => {
    if (message.isSystem) {
      // System notes are the record of every status change. They deliberately do
      // not look like a reply — they are the audit trail that guarantees a
      // submitter is never left wondering what happened, which is the behaviour
      // this whole system replaces.
      return (
        <Stack key={message.id} direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
          <InfoIcon size={14} strokeWidth={1.5} color={theme.palette.text.secondary} />
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {message.body}
          </Typography>
          <Typography variant="body4" color="text.secondary">
            · {relativeOrDateTime(message.createdAt)}
          </Typography>
        </Stack>
      )
    }

    const fromVera = message.author === 'vera'
    return (
      <Card
        key={message.id}
        sx={{
          p: 4,
          mb: 0,
          // Vera's replies are tinted so the two sides of the conversation are
          // distinguishable without reading the attribution line.
          backgroundColor: fromVera ? alpha(theme.palette.brand[500], 0.06) : undefined,
          borderColor: fromVera ? alpha(theme.palette.brand[500], 0.2) : undefined,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body3">
              {fromVera ? message.authorName || 'Vera' : message.authorName || 'You'}
            </Typography>
            {fromVera && (
              <Typography
                variant="body4"
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  color: theme.palette.brand[700],
                  backgroundColor: theme.palette.brand[100],
                }}
              >
                Vera
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body4" color="text.secondary">
              {relativeOrDateTime(message.createdAt)}
            </Typography>
          </Stack>
          <Typography variant="body3" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {message.body}
          </Typography>
          <AttachmentList urls={message.attachmentUrls} />
        </Stack>
      </Card>
    )
  }

  return (
    <Stack spacing={4}>
      {/* The opening message. It lives on the request row rather than in the
          messages table, so a request can never exist without the thing that was
          asked — which is why it is rendered here rather than folded into the
          message list. */}
      <Card sx={{ p: 4, mb: 0 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <StatusChip statuses={SUPPORT_REQUEST_TYPES} status={request.type} size="small" />
            {/* VRG-427: the one place a reader could otherwise mistake an
                automatic report for the submitter's own words. */}
            {request.isSystemGenerated && (
              <SystemGeneratedBadge occurrenceCount={request.occurrenceCount} />
            )}
            {/* One pill, from whichever lifecycle governs this row — the same
                `displayStatusKey` the list uses, so the two cannot disagree about
                the request the user just clicked. Internal callers leave
                `preferBoardStatus` off and keep the request vocabulary. */}
            {preferBoardStatus ? (
              <StatusChip
                statuses={SUPPORT_DISPLAY_STATUSES}
                status={displayStatusKey(request)}
                size="small"
              />
            ) : (
              <StatusChip statuses={statuses} status={request.status} size="small" />
            )}
            <Typography variant="body4" color="text.secondary">
              #{request.reference}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="body4" color="text.secondary">
              {relativeOrDateTime(request.createdAt)}
            </Typography>
          </Stack>

          <Typography variant="body3" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {request.body}
          </Typography>

          <AttachmentList urls={request.attachmentUrls} />

          {/* VRG-721 — the error the user was shown, when this request carries one. */}
          <ReportedErrorBlock request={request} showStaffDetail={showCompany} />

          {/* Auto-captured context. Shown, not hidden: the submitter should be
              able to see exactly what was collected on their behalf, and the
              staffer needs it to reproduce anything. */}
          <Stack spacing={1} sx={{ pt: 2 }}>
            {showCompany && request.companyName && (
              <Typography variant="body4" color="text.secondary">
                Company: {request.companyName}
              </Typography>
            )}
            {/* Hidden when the submitter IS the reader. On your own request "From: you" is a fact
                you already have, and it was the only line in the captured-context block that told
                the viewer nothing. Keyed on identity rather than on which route rendered this, so
                it is right for a staffer reading their OWN request in the company queue too. */}
            {!isOwnRequest && (request.submitterName || request.submitterEmail) && (
              <Typography variant="body4" color="text.secondary">
                From: {request.submitterName ?? 'Unknown'}
                {request.submitterEmail ? ` (${request.submitterEmail})` : ''}
              </Typography>
            )}
            {request.pageUrl && (
              <Typography variant="body4" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                Page: {request.pageUrl}
              </Typography>
            )}
            {request.userAgent && (
              <Typography variant="body4" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                Browser: {request.userAgent}
              </Typography>
            )}
            {/*
              VRG-378 build context, in the same block and the same voice as the
              rest — it is the same KIND of fact (collected on your behalf, not
              typed by you), so splitting it into a separate panel would imply a
              distinction that does not exist.

              Shown to the submitter as well as to staff, deliberately. The block's
              existing rationale is that a submitter should see exactly what was
              collected on their behalf, and quietly attaching the build they were
              running while displaying only the browser would undercut it.

              `Reported from` is omitted for a platform report: on a page inside
              the platform it is the only thing it could be, and a line that can
              only say one thing is noise. It appears the moment it is informative.
            */}
            {buildLines.map(line => (
              <Typography
                key={line}
                variant="body4"
                color="text.secondary"
                sx={{ wordBreak: 'break-all' }}
              >
                {line}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Card>

      {request.messages.map(renderMessage)}

      {/* Last, under the conversation it joins — a composer above the thread would ask
          the user to answer before reading. Always rendered when the caller opts in,
          including on a resolved or closed request: the API reopens a resolved request
          when the customer replies, so a follow-up cannot land in a thread nobody is
          watching, and hiding the box would push the user into raising a duplicate. */}
      {reply && (
        <ReplyComposer
          label={reply.label}
          placeholder={reply.placeholder}
          onSend={reply.onSend}
          isPending={reply.isPending}
        />
      )}
    </Stack>
  )
}

/**
 * VRG-721 — the error behind a report, rendered as facts rather than as a JSON dump.
 *
 * Two kinds of row carry one: a person's request opened from an error toast's
 * "Report a problem" (`clientContext.error`), and the quiet automatic report the
 * same toast filed. A render crash carries `errorMessage` / `componentStack`
 * instead. All three render here, in the same voice as the captured-context
 * block below — collected on the reporter's behalf, not typed by them.
 *
 * The `ref` gets a copy button because it is the one value a staffer pastes
 * somewhere else (the log explorer) rather than reads.
 *
 * Staff-only detail — the component/JS stack and the link to the matching
 * automatic report — sits behind `showStaffDetail`. A brand's user cannot open an
 * unclaimed automatic report, so linking to one from their own thread would be a
 * link to a 404; and a stack trace is noise to a customer.
 */
const ReportedErrorBlock = ({
  request,
  showStaffDetail,
}: {
  request: SupportRequestDetail
  showStaffDetail: boolean
}) => {
  const theme = useTheme()
  const ctx = (request.clientContext ?? {}) as Record<string, any>
  const error = ctx.error && typeof ctx.error === 'object' ? (ctx.error as Record<string, any>) : null
  const crashMessage = typeof ctx.errorMessage === 'string' ? ctx.errorMessage : null
  const stack = typeof ctx.componentStack === 'string' ? ctx.componentStack : null
  const linkedId = typeof ctx.linkedAutoReportId === 'string' ? ctx.linkedAutoReportId : null
  const linkedRef = typeof ctx.linkedAutoReportReference === 'number' ? ctx.linkedAutoReportReference : null

  if (!error && !crashMessage && !(showStaffDetail && linkedId)) return null

  const shown = error ? [error.title, error.text].filter(Boolean).join(' · ') : null
  const requestLine = error
    ? [
        [error.method, error.path].filter(Boolean).join(' '),
        typeof error.status === 'number' ? String(error.status) : error.kind === 'network' ? 'no response' : null,
        error.code ?? null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null
  // VRG-726 — the first occurrence's ref (the sample the report was filed with) and, on an
  // automatic report that recurred, the latest one's. Staff get both, each with "Find in logs".
  const { first: ref, latest: latestRef } = errorRefsOf(request)
  const kindLabel: Record<string, string> = {
    toast: 'Error message shown',
    uncaught: 'Uncaught error',
    rejection: 'Unhandled error',
    render: 'Page crash',
  }
  const heading = (typeof ctx.reportKind === 'string' && kindLabel[ctx.reportKind]) || 'Error'

  return (
    <Box
      data-testid="support-reported-error"
      sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.primary[100]}` }}
    >
      <Stack spacing={1.5}>
        <Typography
          variant="body4"
          color="text.secondary"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {heading}
          {request.isSystemGenerated && <InfoTooltip term="support-automatic-report" />}
        </Typography>
        {shown && (
          <Typography variant="body3" sx={{ lineHeight: 1.5, overflowWrap: 'anywhere' }}>
            {shown}
          </Typography>
        )}
        {!error && crashMessage && (
          <Typography variant="body3" sx={{ lineHeight: 1.5, overflowWrap: 'anywhere' }}>
            {crashMessage}
          </Typography>
        )}
        {requestLine && (
          <Typography variant="body4" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            Request: {requestLine}
          </Typography>
        )}
        {ref && (
          <CopyableRef
            value={ref}
            label={showStaffDetail && latestRef ? 'First ref' : 'Ref'}
            logsUrl={showStaffDetail ? cloudLoggingUrl(ref) : undefined}
          />
        )}
        {showStaffDetail && latestRef && (
          <CopyableRef value={latestRef} label="Latest ref" logsUrl={cloudLoggingUrl(latestRef)} />
        )}
        {showStaffDetail && ref && (
          // Said once, under the refs, rather than as a tooltip: it is the instruction for
          // the next step of triage, and it has to work when the link does not (a staffer
          // without console access to the project still knows where to paste the ref).
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Find in logs opens Cloud Logging (project {CLOUD_LOGGING_PROJECT}) searching for the
            ref in quotes, which matches the failure's log entry and its stack trace. Without
            access, paste <Box component="span" sx={{ fontFamily: 'monospace' }}>"{latestRef ?? ref}"</Box>{' '}
            into the Logs Explorer query box.
            {latestRef ? ' The latest ref is the most recent occurrence, so it is the one still inside log retention.' : ''}
          </Typography>
        )}
        {error?.at && (
          <Typography variant="body4" color="text.secondary">
            Seen: {relativeOrDateTime(error.at)}
          </Typography>
        )}
        {showStaffDetail && error?.attribution === 'recent' && (
          // Stated, not hidden: this match is a heuristic (the newest failure within a few
          // seconds of the toast), and a staffer should weigh it as one.
          <Typography variant="body4" color="text.secondary">
            Matched to the toast by timing, not by message.
          </Typography>
        )}
        {showStaffDetail && linkedId && (
          <Typography variant="body4" color="text.secondary">
            Automatic report:{' '}
            <Link component={RouterLink} to={ROUTES.INTERNAL_SUPPORT_REQUEST(linkedId)}>
              #{linkedRef ?? '…'}
            </Link>
          </Typography>
        )}
        {showStaffDetail && stack && (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              maxHeight: 240,
              overflow: 'auto',
              borderRadius: 1,
              fontSize: 12,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
            }}
          >
            {stack}
          </Box>
        )}
      </Stack>
    </Box>
  )
}

const CopyableRef = ({
  value,
  label = 'Ref',
  logsUrl,
}: {
  value: string
  label?: string
  /** VRG-726 — staff only: a Cloud Logging search for this ref. */
  logsUrl?: string
}) => {
  const [copied, setCopied] = useState(false)
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
      <Typography
        variant="body4"
        color="text.secondary"
        sx={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {label}
        <InfoTooltip term="support-error-reference" />
        <Box component="span" sx={{ ml: 1, fontFamily: 'monospace', color: 'text.primary' }}>
          {value}
        </Box>
      </Typography>
      <Tooltip title={copied ? 'Copied' : 'Copy ref'}>
        <IconButton
          size="small"
          aria-label="Copy ref"
          onClick={() => {
            try {
              void navigator.clipboard.writeText(value)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1500)
            } catch {
              /* clipboard unavailable — the ref is still on screen to select */
            }
          }}
        >
          {copied ? <CheckIcon size={14} strokeWidth={1.5} /> : <CopyIcon size={14} strokeWidth={1.5} />}
        </IconButton>
      </Tooltip>
      {logsUrl && (
        <Link
          href={logsUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="body4"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}
        >
          Find in logs
          <ExternalLinkIcon size={12} strokeWidth={1.5} />
        </Link>
      )}
    </Stack>
  )
}
